<script setup lang="ts">
import InternalLinks from "@/components/seo/InternalLinks.vue";
import ZakatAssetsBreakdown from "@/components/zakat/ZakatAssetsBreakdown.vue";
import NisabInfo from "@/components/zakat/NisabInfo.vue";
import ZakatFAQ from "@/components/zakat/ZakatFAQ.vue";
import ZakatInput from "@/components/zakat/ZakatInput.vue";
import ZakatResult from "@/components/zakat/ZakatResult.vue";
import { seoConfig } from "@/config/seo.config";
import { useZakat } from "@/composables/useZakat";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { messages, locale } = useI18n();

const zakatPage = computed(() => messages.value[locale.value]?.pages?.zakat);

const {
  amount,
  goldTola,
  silverTola,
  metalRates,
  goldValue,
  silverValue,
  effectiveAmount,
  selectedNisab,
  amountInWords,
  currentNisab,
  formattedCurrentNisab,
  isEligible,
  formattedZakat,
  setAmount,
  increment,
  decrement,
  setGoldTola,
  setSilverTola,
  toggleNisab,
  reset,
} = useZakat();

const ratesUpdatedLine = computed(() => {
  const raw = metalRates.value.lastUpdated;
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T00:00:00Z` : raw;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return "";

  const formatted = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);

  return `Gold & silver rates updated on ${formatted}.`;
});

const showRatesStaleWarning = computed(() => {
  const raw = metalRates.value.lastUpdated;
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T00:00:00Z` : raw;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return false;

  const diffMs = Date.now() - date.getTime();
  const daysOld = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  return daysOld > 14;
});

useHead({
  title: seoConfig.zakat.title,
  meta: [
    {
      name: "description",
      content: seoConfig.zakat.description,
    },
    {
      name: "keywords",
      content:
        "zakat calculator pakistan, online zakat calculator pkr, pakistan zakat calculator 2026, zakat ka hisaab pakistan, simple zakat calculator pakistan, roman urdu zakat calculator, nisab calculator pakistan, zakat 2.5% calculator",
    },
    { property: "og:title", content: seoConfig.zakat.title },
    { property: "og:description", content: seoConfig.zakat.description },
    { property: "og:type", content: "website" },
    { property: "og:url", content: seoConfig.zakat.canonical },
    { property: "og:site_name", content: "Rozay" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: seoConfig.zakat.title },
    { name: "twitter:description", content: seoConfig.zakat.description },
  ],
  link: [
    {
      rel: "canonical",
      href: seoConfig.zakat.canonical,
    },
  ],
});
</script>

<template>
  <section
    v-if="zakatPage"
    class="w-full max-w-full sm:max-w-xl mx-auto px-4 py-5 space-y-4"
  >
    <header class="space-y-1 text-center">
      <h1 class="text-lg sm:text-2xl font-semibold leading-tight text-gray-900 dark:text-gray-100">
        Zakat Calculator Pakistan
      </h1>
      <p class="text-xs sm:text-sm text-gray-600 dark:text-gray-300 leading-snug">
        Calculate 2.5% zakat instantly (PKR).
      </p>
    </header>

    <ZakatInput
      :amount="amount"
      :amount-in-words="amountInWords"
      :input-label="zakatPage.inputLabel"
      :reset-label="zakatPage.reset"
      @set-amount="setAmount"
      @increment="increment"
      @decrement="decrement"
      @reset="reset"
    />

    <ZakatAssetsBreakdown
      :gold-tola="goldTola"
      :silver-tola="silverTola"
      :gold-per-tola="metalRates.goldPerTola"
      :silver-per-tola="metalRates.silverPerTola"
      :gold-value="goldValue"
      :silver-value="silverValue"
      @update:gold-tola="setGoldTola"
      @update:silver-tola="setSilverTola"
    />

    <NisabInfo
      :amount="effectiveAmount"
      :selected-nisab="selectedNisab"
      :nisab-label="zakatPage.nisabLabel"
      :silver-label="zakatPage.silver"
      :gold-label="zakatPage.gold"
      :nisab-switch="zakatPage.nisabSwitch"
      :nisab-switch-back="zakatPage.nisabSwitchBack"
      :educational-line="zakatPage.educationalLine"
      :formatted-current-nisab="formattedCurrentNisab"
      @toggle-nisab="toggleNisab"
    />

    <ZakatResult
      :amount="effectiveAmount"
      :current-nisab="currentNisab"
      :is-eligible="isEligible"
      :formatted-zakat="formattedZakat"
      :below-nisab="zakatPage.belowNisab"
      :result-title="zakatPage.resultTitle"
      :result-note="zakatPage.resultNote"
    />

    <p v-if="ratesUpdatedLine" class="text-xs text-gray-500 dark:text-gray-400">
      {{ ratesUpdatedLine }}
    </p>

    <p
      v-if="showRatesStaleWarning"
      class="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md px-3 py-2"
    >
      Rates may have changed. Please verify current market price if calculating
      large amount.
    </p>

    <p class="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
      {{ zakatPage.seoSupportBlock }}
    </p>

    <div class="border-t border-gray-200 dark:border-gray-700"></div>

    <section class="space-y-4">
      <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
        Zakat FAQs
      </h2>
      <ZakatFAQ />
    </section>

    <p class="text-xs text-gray-500 dark:text-gray-400 text-center leading-relaxed">
      {{ zakatPage.disclaimer }}
    </p>

    <InternalLinks current="zakat" />
  </section>
</template>
